/**
 * Client for the Cloudflare Worker (diet-app-api). Sends the user's Supabase
 * access token; the Worker verifies it and applies RLS when it reads data.
 */
import { config } from '../config';
import { log } from '../lib/logger';

export class ApiError extends Error {
  public readonly status: number;
  public readonly code: string;
  public readonly requestId: string | null;

  constructor(status: number, code: string, message: string, requestId: string | null) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.requestId = requestId;
  }
}

interface ErrorBody {
  error?: { code?: string; message?: string; requestId?: string };
}

export async function callWorker<TResponse>(path: string, body: unknown, accessToken: string, timeoutMs: number = 45_000, method: 'GET' | 'POST' = 'POST'): Promise<TResponse> {
  const url: string = `${config.apiBaseUrl}${path}`;
  const controller: AbortController = new AbortController();
  const timer: ReturnType<typeof setTimeout> = setTimeout(() => controller.abort(), timeoutMs);
  const started: number = performance.now();
  try {
    const res: Response = await fetch(url, method === 'GET'
      ? { method, headers: { Authorization: `Bearer ${accessToken}` }, signal: controller.signal }
      : { method, headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${accessToken}` }, body: JSON.stringify(body), signal: controller.signal });
    const requestId: string | null = res.headers.get('X-Request-Id');
    const text: string = await res.text();
    let json: unknown = null;
    try {
      json = text.length > 0 ? JSON.parse(text) : null;
    } catch (parseErr) {
      log.error('worker.bad_json', { path, status: res.status, requestId, parseErr: String(parseErr), bodyStart: text.slice(0, 200) });
      throw new ApiError(res.status, 'bad_response', `The server sent an unreadable reply (HTTP ${res.status}).`, requestId);
    }
    if (!res.ok) {
      const e: ErrorBody = (json ?? {}) as ErrorBody;
      const code: string = e.error?.code ?? 'http_error';
      const message: string = e.error?.message ?? `Request failed (HTTP ${res.status}).`;
      log.warn('worker.error', { path, status: res.status, code, message, requestId: e.error?.requestId ?? requestId });
      throw new ApiError(res.status, code, message, e.error?.requestId ?? requestId);
    }
    log.info('worker.ok', { path, status: res.status, requestId, ms: Math.round(performance.now() - started) });
    return json as TResponse;
  } catch (err) {
    if (err instanceof ApiError) throw err;
    if (err instanceof DOMException && err.name === 'AbortError') {
      log.warn('worker.timeout', { path, timeoutMs });
      throw new ApiError(0, 'timeout', 'The server took too long to answer. Try again.', null);
    }
    log.error('worker.network_error', { path, error: String(err) });
    throw new ApiError(0, 'network_error', 'Could not reach the server. Check your connection.', null);
  } finally {
    clearTimeout(timer);
  }
}
